import EventEmitter from 'event-emitter';
import Hammer from 'hammerjs';
import log from 'loglevel';
import DocModuler from '../DocModuler';
import { getBoundaryValue } from '../utils/tool';

/**
 * 手势插件
 *
 * @export
 * @class GesturePlugin
 */
export default class GesturePlugin {
  constructor(store) {
    EventEmitter(this);
    this.store = store;
    this.hammer = null;
    this.e = {
      pan: this._pan.bind(this),
      pinch: this._pinch.bind(this),
      tap: this._tap.bind(this),
      pinchend: this._pinchend.bind(this),
      nextValue: (arr, currentValue) => {
        let i = arr.indexOf(currentValue);
        if (arr.indexOf(currentValue) < 0) {
          i = arr.indexOf(1);
        }

        i += 1;
        i = Math.max(Math.min(i, arr.length - 1), 0);
        return arr[i];
      },
      prevValue: (arr, currentValue) => {
        let i = arr.indexOf(currentValue);
        if (arr.indexOf(currentValue) < 0) {
          i = arr.indexOf(1);
        }

        i -= 1;
        i = Math.max(Math.min(i, arr.length - 1), 0);
        return arr[i];
      },
      mouseWheelHandeler: this._mouseWheelHandeler.bind(this)
    };

    this._scale = 1;
    this._oldScale = 1;
    this._top = 0;
    this._left = 0;
  }

  zoom(factor, x, y) {
    this._left = 0;
    this._top = 0;
    this._updateTransform();

    this.lastOffsetX = 0;
    this.lastOffsetY = 0;
    if (factor >= this._scale) {
      this._scale = factor;
      this._left += x * (factor - this._scale);
      this._top += y * (factor - this._scale);
    } else {
      // 缩小
      this._scale = factor;
      this._left += this._left * (factor - this._scale);
      this._top += this._top * (factor - this._scale);
    }
    this._updateTransform();

    this.store.setKV('scaleFactor', factor);
    // 触发缩放比事件
    this._scaleTriggerEvent();
  }

  /**
   * 销毁
   *
   * @memberof GesturePlugin
   */
  destroy() {
    this.hammer && this.hammer.destroy();
  }

  /**
   * 复位
   *
   * @memberof GesturePlugin
   */
  reset() {
    if (!this.hammer) {
      log.warn('gesture have not be initialized');
      return;
    }

    if (!this.hammer.element) {
      log.warn('gesture target: undefined');
      return;
    }

    this._scale = 1;
    this._oldScale = 1;

    if (DocModuler.rotateDeg != 0) {
      let rect = this.hammer.element.getBoundingClientRect();
      let originX = rect.x + rect.width * 0.5;
      let originY = rect.y + rect.height * 0.5;
      let centerX = window.innerWidth / 2;
      let centerY = window.innerHeight / 2;

      let deltaX = centerX - originX;
      let deltaY = centerY - originY;
      // if (deltaX.toFixed(2) == 0 && deltaY.toFixed(2) == 0) {
      //   return;
      // }
      this._left = deltaX + this.lastOffsetX;
      this._top = deltaY + this.lastOffsetY;

      this.lastOffsetX = this._left;
      this.lastOffsetY = this._top;
    } else {
      this._left = 0;
      this._top = 0;
      this.lastOffsetX = 0;
      this.lastOffsetY = 0;
    }
    this._updateTransform();

    // 触发缩放比事件
    this._scaleTriggerEvent();
  }

  /**
   * 设置手势库
   *
   * @param {*} element
   * @memberof GesturePlugin
   */
  install(element) {
    if (!element) {
      log.error('gesture install failed, root element is null');
      return;
    }

    if (this.hammer) {
      this.hammer.destroy();
      this.hammer = null;
    }

    this.hammer = new Hammer(element);
    // 默认情况下 pinch 与 rotate 是禁用的，因为它们会阻塞元素
    this.hammer.get('pinch').set({ enable: true });
    this.hammer.add(new Hammer.Pan());
    this.hammer.add(
      new Hammer.Pinch({
        threshold: 0.1
      })
    );

    this.hammer.on('panstart panmove panend', this.e.pan);
    this.hammer.on('pinch', this.e.pinch);
    this.hammer.on('pinchend', this.e.pinchend);
    this.hammer.on('tap', this.e.tap);

    // 是否支持移动边界
    this.isSuportMoveBoundary = this.checkGrandParentNode();

    this.lastOffsetX = 0;
    this.lastOffsetY = 0;
  }
  /**
   * 增加鼠标滚动缩放监听
   */
  addMouseWheel(element) {
    this.dragEl = element;
    if (!this.initHeight || !this.initWidth) {
      const { width: initWidth, height: initHeight } = this.dragEl.getBoundingClientRect();
      this.initWidth = initWidth;
      this.initHeight = initHeight;
    }
    element.removeEventListener('mousewheel', this.e.mouseWheelHandeler);
    element.addEventListener('mousewheel', this.e.mouseWheelHandeler);
  }

  _mouseWheelHandeler(ev) {
    this.dragEl.style.transformOrigin = '0 0';

    const pDom = this.dragEl.parentElement;
    const isZoomOut = ev.deltaY > 0; // 缩小
    // 鼠标坐标
    const { x: mouseX, y: mouseY } = ev;
    // 元素当前宽高
    const { height, width } = this.dragEl.getBoundingClientRect();
    const { top: pTop, left: pLeft } = pDom.getBoundingClientRect();
    let factor = null;
    let currentFactor = this.store.getKV('scaleFactor');
    if (isZoomOut) {
      // 缩小
      factor = this.e.prevValue(DocModuler.zoomFactors, currentFactor);
    } else {
      // 放大
      factor = this.e.nextValue(DocModuler.zoomFactors, currentFactor);
    }
    this._scale = factor;
    this.store.setKV('scaleFactor', factor);

    // 获取比例
    let yScale = (mouseY - pTop - this.lastOffsetY) / height;
    let xScale = (mouseX - pLeft - this.lastOffsetX) / width;
    // 放大后的宽高
    const ampWidth = this.initWidth * factor;
    const ampHeight = this.initHeight * factor;
    // 需要重新运算的 translate 坐标
    const y = yScale * (ampHeight - height);
    const x = xScale * (ampWidth - width);

    // 更新
    this._top = this.lastOffsetY - y;
    this._left = this.lastOffsetX - x;

    this.lastOffsetX = this._left;
    this.lastOffsetY = this._top;

    this._updateTransform();

    // 触发缩放比事件
    this._scaleTriggerEvent();

    ev.preventDefault();
  }

  _pan(e) {
    // x,y轴偏移量
    const offsetX = e.deltaX + this.lastOffsetX;
    const offsetY = e.deltaY + this.lastOffsetY;

    if (this.isSuportMoveBoundary) {
      if (e.type === 'panstart') {
        // 超出祖父元素最大占比
        const ratio = 0.8;

        // dragArea
        const dragRect = this.hammer.element.getBoundingClientRect();
        // dragArea的祖父元素
        const grandparentRect = this.hammer.element.parentElement.parentElement.getBoundingClientRect();

        // 计算tranlateX左右各移动多少距离到达临界点
        const diffLeftTranslateX = grandparentRect.x - dragRect.x;
        const diffRightTranslateX = grandparentRect.x + grandparentRect.width - dragRect.x;

        // 计算tranlateX左Y右各移动多Y少距离到达临界点
        const diffLeftTranslateY = grandparentRect.y - dragRect.y;
        const diffRightTranslateY = grandparentRect.y + grandparentRect.height - dragRect.y;

        // 计算 translateX和translateY的临界点
        this.minTranslateX = diffLeftTranslateX + this.lastOffsetX - dragRect.width * ratio;
        this.maxTranslateX = diffRightTranslateX + this.lastOffsetX - dragRect.width * (1 - ratio);
        this.minTranslateY = diffLeftTranslateY + this.lastOffsetY - dragRect.height * ratio;
        this.maxTranslateY = diffRightTranslateY + this.lastOffsetY + dragRect.height * ratio;
      }

      // 获取translateX,translateY的边界值范围内的取值
      this._left = getBoundaryValue(this.minTranslateX, this.maxTranslateX, offsetX);
      this._top = getBoundaryValue(this.minTranslateY, this.maxTranslateY, offsetY);
    } else {
      this._left = offsetX;
      this._top = offsetY;
    }

    if (e.type === 'panend') {
      this.lastOffsetX = this._left;
      this.lastOffsetY = this._top;
    }

    this._updateTransform();
  }

  _pinch(e) {
    this._scale = e.scale * this._oldScale;
    this._updateTransform();
  }

  _pinchend(e) {
    this._oldScale *= e.scale;
  }

  _tap(e) {
    const { srcEvent } = e;
    let xPos = srcEvent['offsetX'] || 0;
    let yPos = srcEvent['offsetY'] || 0;

    this.emit('tap', { xPos, yPos });
  }

  _updateTransform() {
    let a = this._scale;
    let d = this._scale;
    let tx = this._left;
    let ty = this._top;

    if (!this.hammer || !this.hammer.element) {
      return;
    }

    let el = this.hammer.element;
    let deg = DocModuler.rotateDeg || 0;
    el.style.transform = `matrix(${a},0 ,0 ,${d},${tx},${ty}) rotate(${deg}deg)`;
  }

  /**
   * 触发缩放比事件(只要有调用就触发，缩放比无变化也会触发)
   */
  _scaleTriggerEvent() {
    if (this.store && this.store.info && this.store.info.ee) {
      this.store.info.ee.emitter.emit('scaleTriggerEvent', {
        cid: this.store.info.cid,
        scaleFactor: this.store.info.scaleFactor
      });
    }
  }

  /**
   * 验证拖动边界的父容器
   */
  checkGrandParentNode() {
    // dragArea
    const dragRect = this.hammer.element.getBoundingClientRect();
    // dragArea的祖父元素
    const grandparentRect = this.hammer.element.parentElement.parentElement.getBoundingClientRect();

    return grandparentRect.width >= dragRect.width && grandparentRect.height >= dragRect.height;
  }
}
